// Browser-side helper for Web Push subscription. Lifecycle:
//
//   isPushSupported() → true on Chrome / Edge / Firefox / Safari 16+
//   permissionState() → "granted" | "denied" | "default"
//   subscribeForPush(vapidPublicKey) → registers + posts to API
//   unsubscribeFromPush() → unregisters + posts to API
//
// Used by NotificationsModal's "Включить push" CTA. The CTA only
// renders when isPushSupported && permission !== "denied".
import { api } from "./api.js";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function permissionState() {
  if (!("Notification" in window)) return "denied";
  return Notification.permission; // "granted" | "denied" | "default"
}

// Convert the URL-safe-base64 VAPID public key into the Uint8Array
// that PushManager.subscribe expects.
function urlBase64ToUint8(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Subscribe this browser for push. Steps:
 *  1. Request Notification.permission if not yet granted.
 *  2. Wait for the registered service worker to be ready.
 *  3. Call pushManager.subscribe with the VAPID public key.
 *  4. Persist the subscription on the API side.
 *
 * Returns the PushSubscription on success, or throws with a clear
 * message the UI can surface.
 */
export async function subscribeForPush(vapidPublicKey) {
  if (!isPushSupported())
    throw new Error("Браузер не поддерживает push-уведомления");
  if (!vapidPublicKey)
    throw new Error("Push не настроен на сервере");

  if (Notification.permission === "default") {
    const p = await Notification.requestPermission();
    if (p !== "granted") throw new Error("Разрешение отклонено");
  } else if (Notification.permission !== "granted") {
    throw new Error("Разрешение на уведомления отклонено");
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8(vapidPublicKey),
    });
  }
  const json = sub.toJSON();
  await api.registerPushSubscription({
    endpoint: json.endpoint,
    keys: json.keys,
    userAgent: navigator.userAgent || null,
  });
  return sub;
}

/**
 * Unregister this browser's subscription, both locally and on the
 * server. Safe to call when there's no active subscription.
 */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch {}
  try { await api.unregisterPushSubscription({ endpoint }); } catch {}
}

// Convenience: kick off auto-subscribe if the user already granted
// permission on a previous visit AND the server has a VAPID public
// key. Idempotent — re-running just refreshes last_seen_at.
export async function ensureSubscribedIfGranted(vapidPublicKey) {
  if (!isPushSupported() || !vapidPublicKey) return;
  if (Notification.permission !== "granted") return;
  try { await subscribeForPush(vapidPublicKey); } catch {}
}
